import { NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import {
  CookieService,
  LayoutService,
  NotificationService,
  OpenReplayService,
  ResponsiveService,
  injectEnvironment,
} from '@cf/core';
import { OrgRouterHelperService } from '@cf/features/organizations/admin';
import { ToastContainerComponent } from '@cf/shared/toast';
import { PullToRefreshComponent } from '@cf/shared/ui/loading';
import {
  AccountService,
  AuthService,
  CalendarStoreService,
  FirstDayOfWeekEnum,
  IAccount,
  PaymentStoreService,
  TIME_CONFIG,
  TimeFormatEnum,
  detectBrowserLanguage,
  firstMonday,
  firstSaturday,
  firstSunday,
  loadInvitations,
  manageCalendarsSuccess,
  manageInvitationByToken,
  manageInvitationError,
  manageInvitationRedirect,
} from '@cf/temp/services';
import { NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { ofType } from '@ngneat/effects';
import { Actions } from '@ngneat/effects-ng';
import { TranslateService } from '@tolgee/ngx';
import { Settings } from 'luxon';
import { NgxSonnerToaster, ToastOptions } from 'ngx-sonner';
import { fromEvent, skip } from 'rxjs';
import { debounceTime, filter, startWith } from 'rxjs/operators';
import { HeaderUserFlowComponent } from './modules/core/layout/components/header-user-flow/header-user-flow.component';
import { HeaderComponent } from './modules/core/layout/components/header/header.component';
import { SidenavComponent } from './modules/core/layout/components/sidenav/sidenav.component';
import { SidenavService } from './modules/core/layout/components/sidenav/sidenav.service';
import { FooterComponent } from './modules/widgets/footer/footer.component';

@Component({
  selector: 'cf-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    ToastContainerComponent,
    PullToRefreshComponent,
    NgIf,
    HeaderComponent,
    SidenavComponent,
    HeaderUserFlowComponent,
    RouterOutlet,
    FooterComponent,
    NgxSonnerToaster,
  ],
})
export class AppComponent implements OnInit {
  private readonly notificationService = inject(NotificationService);
  private readonly calendarStoreService = inject(CalendarStoreService);
  private readonly paymentStoreService = inject(PaymentStoreService);
  private readonly layoutService = inject(LayoutService);
  private readonly responsiveService = inject(ResponsiveService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly zone = inject(NgZone);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  public authService = inject(AuthService);
  private accountService = inject(AccountService);
  modalConfig = inject(NgbModalConfig);
  private readonly route = inject(ActivatedRoute);

  #cookieService = inject(CookieService);
  translateService = inject(TranslateService);
  #openReplayService = inject(OpenReplayService);
  #environment = injectEnvironment();
  #sidenavService = inject(SidenavService);
  #orgRouterHelperService = inject(OrgRouterHelperService);

  title = 'cal-frenzy';

  isAuthorized = this.authService.isAuthorized;

  showSidenav = false;

  hideHeader!: boolean;

  private environment = injectEnvironment();
  appStartDate = new Date();
  actions = inject(Actions);

  toastOptions: ToastOptions = {
    classes: {
      actionButton: 'tw:!bg-transparent tw:!text-current tw:!border tw:!border-current',
    },
  };

  constructor() {
    this.accountService.getAccountProfile();

    fromEvent(window, 'online')
      .pipe(debounceTime(400), takeUntilDestroyed())
      .subscribe(() => {
        this.accountService.getAccountProfile();
      });

    this.route.queryParams.subscribe((params) => {
      this.joinOrganizationHandler(params['joinOrganizationToken']);
      if (params['is_google_login']) {
        this.#orgRouterHelperService.navigate(['/dashboard']);
      }
    });
    this.prepareTimeConfig(this.accountService.account);
    if (this.environment.production) {
      this.checkNewVersion();
    }
    this.accountService.account$.pipe(skip(1)).subscribe((res) => {
      this.prepareTimeConfig(res);
      this.accountService.configUpdated$.next(true);
    });

    this.modalConfig.backdrop = 'static';
    this.modalConfig.keyboard = false;
    this.modalConfig.centered = true;

    if (this.#environment.envName === 'prod') {
      this.startOpenReplay();
    }
  }

  private startOpenReplay() {
    this.#openReplayService.start();
    this.accountService.account$.subscribe((account) => {
      if (account) {
        this.#openReplayService.setUserData({ id: account.email });
      }
    });
  }

  joinOrganizationHandler(token: string) {
    if (token) {
      this.actions.dispatch(manageInvitationByToken({ token }));
    }
  }

  ngOnInit() {
    this.accountService.account$.pipe(startWith(null)).subscribe((account) => {
      const language =
        account?.language ??
        (this.#cookieService.get('default_language') || detectBrowserLanguage());
      this.translateService.changeLanguage(language);
      this.#cookieService.setWildcardDomain('default_language', language);
      Settings.defaultLocale = language;
    });
    if (this.authService.isAuthorized()) {
      this.calendarStoreService.getCalendars();
      this.paymentStoreService.getPaymentList();

      this.paymentStoreService.broadcastChannel.onmessage = () => {
        this.paymentStoreService.getPaymentList();
      };
    }

    this.responsiveService.isMobile$.subscribe((isMobile) => {
      if (!isMobile) {
        this.showSidenav = false;
      }
    });

    this.layoutService.hideHeader.subscribe((value) => {
      this.hideHeader = value;
      this.cd.detectChanges();
    });

    this.actions.pipe(ofType(manageInvitationError)).subscribe((res) => {
      const route =
        res.status === 'REVOKED'
          ? `error/${res.status}`
          : res.status === 'ACCEPTED' && this.authService.isAuthorized()
            ? '/dashboard'
            : '/auth/login';
      this.router.navigate([route]);
    });
    this.actions.pipe(ofType(manageInvitationRedirect)).subscribe((res) => {
      if (res.isNewUser) {
        this.router.navigate(['/auth/sign-up'], { queryParams: { email: res.email } });
      } else {
        this.router.navigate(['/auth/login'], { queryParams: { email: res.email } });
      }
    });
    this.actions.pipe(ofType(manageCalendarsSuccess)).subscribe(() => {
      this.#orgRouterHelperService.navigate(['/dashboard']);
      this.actions.dispatch(loadInvitations());
    });
  }

  login(): void {
    this.router
      .navigateByUrl('/')
      .then(() => setTimeout(() => void this.router.navigate(['/auth/login']), 0));
  }

  register(): void {
    this.router
      .navigateByUrl('/')
      .then(() => setTimeout(() => void this.router.navigate(['/auth/sign-up']), 0));
  }

  logout(): void {
    this.authService.logout();
  }

  private checkNewVersion() {
    self.addEventListener('install', (e: any) => {
      e.waitUntil(Promise.all([(self as any).skipWaiting()]).then());
    });
    self.addEventListener('activate', (e: any) => {
      e.waitUntil(Promise.all([(self as any).clients.claim()]));
    });

    this.swUpdate.versionUpdates
      // eslint-disable-next-line no-untranslated-string/no-untranslated-string
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        const currentDate = new Date();
        const diff = currentDate.getTime() - this.appStartDate.getTime();
        // if the app was started less than 2 minutes ago, reload the page
        this.swUpdate.activateUpdate().then(() => {
          if (diff < 2 * 60 * 1000) {
            document.location.reload();
          } else {
            this.showNotification();
          }
        });
      });
  }

  private showNotification() {
    this.notificationService.action(
      this.translateService.instant('new_version_available'),
      '',
      {
        label: this.translateService.instant('reload_page'),
        onClick: () => {
          document.location.reload();
        },
      },
      false,
    );
  }

  // async checkExtension(id = 'igmlifilbgnlaijamohaakgoacoeoidk') {
  //   try {
  //     await fetch(`chrome-extension://${id}/index.html`);
  //     return true;
  //   } catch (e) {
  //     if (e instanceof TypeError && e.message == 'Failed to fetch') {
  //       return false;
  //     } else {
  //       throw e;
  //     }
  //   }
  // }

  toggleSidenav() {
    this.#sidenavService.toggleOpened();
  }

  private prepareTimeConfig(account: IAccount): void {
    this.zone.run(() => {
      if (account) {
        if (account.first_day_of_week === FirstDayOfWeekEnum.Sunday) {
          TIME_CONFIG.weeks = firstSunday;
        }
        if (account.first_day_of_week === FirstDayOfWeekEnum.Monday) {
          TIME_CONFIG.weeks = firstMonday;
        }
        if (account.first_day_of_week === FirstDayOfWeekEnum.Saturday) {
          TIME_CONFIG.weeks = firstSaturday;
        }
        if (account.timezone) {
          TIME_CONFIG.timezone = account.timezone;
        }
        if (account.time_format === TimeFormatEnum['12h']) {
          TIME_CONFIG.timeFormat = 12;
          TIME_CONFIG.defaultStartTime = '09:00 AM';
          TIME_CONFIG.defaultEndTime = '05:00 PM';
        } else {
          TIME_CONFIG.timeFormat = 24;
          TIME_CONFIG.defaultStartTime = '09:00';
          TIME_CONFIG.defaultEndTime = '17:00';
        }
      }
    });
  }
}
